// QuestionsDialog.js
// The questions a check raised about a batch, and the person's answers to them
// (Spec 3 section 7.2). Opened from the batch panel's Resolve conflicts while
// the batch is parked; or, after landing, read-only on one question from a
// unit's correction records (`readOnly`).
//
// One prompt per stable subject. An ordinary prompt is about one incoming row:
// its Conflicts (the row's identifier and profile as answers leave it, the rows
// already under that identifier, the look-alikes its details match) and its
// Resolutions, checkbox-style and mutually exclusive, each with the
// assignments it makes and the files they reach. A prompt whose saved answer's
// rows changed (evidence_changed) shows the saved and current rows and asks,
// for each affected assignment, to reconfirm, revise, keep the literal value
// or withdraw it. Every choice becomes updates of the stable subjects it
// affects (services/answers.js::resolveChoice); a saved answer those updates
// disturb is listed for a decision of its own; Submit stages the lot.
//
// The dialog pages the open prompts by open_at and reads every record of the
// batch, the roster a choice's complete scope comes from. It follows the batch
// document: while another user submits, or the batch is queued or checked
// again, every answer control is disabled and the person's choices are kept;
// when the batch parks again, a choice is kept only where its prompt is
// unchanged. In-flight reads for an earlier park, or a closed dialog, are
// discarded.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  ANOTHER_SUBMISSION, PLACEHOLDER_PREFIX, affectedGroups, dependentSlots, draftAnswers, loadRoster, newSubmission,
  ownEvidence, ownReference, pageQuestions, recordsBySubject, replacementAnswer, resolveChoice, subjectOfReference,
  submitAnswers,
} from '../../../services/answers';
import { gestureFailureMessage } from '../../../services/cleaningGestures';
import { useAuth } from '../../../contexts/AuthContext';
import { tableScope } from '../../../utils/blobPaths';
import { canonicalJson } from '../../../utils/answerKeys';
import { rowKeyText } from '../../../utils/decisions';
import { countNoun } from '../../../utils/pluralise';
import { stableStringify } from '../../../utils/stableJson';
import { formatTimestamp } from '../../../utils/timestamps';
import { formatText } from '../../common/TextFormatterProvider';
import { targetLabel } from './UnitStepFrame';

export const NOT_SAVED = 'Your answers were not saved.';
export const CHANGED_SINCE_CHOSEN = 'This question changed since you chose an answer. Read it again.';
const CHOOSE_CARRIED = 'Choose what happens to the rows with no details.';
const CHOOSE_SOURCE = 'Choose the row to take this from.';
const CHOOSE_TARGETS = 'Choose the rows to move.';

// The carried rows offered for ticking per page: the prompt's own display cap
// (CARRIED_SHOWN in ingestion/walk/resolution.py).
const CARRIED_PAGE_SIZE = 200;

// How a saved assignment's choice reads in its history.
const CHOICE_TEXT = {
  own: 'Its own identifier confirmed',
  typed: 'An identifier typed',
  join: 'A listed identifier given',
  pull: 'Given another row\'s identifier',
  move_here: 'Moved to another row\'s profile',
  move_there: 'Moved to the profile it was compared with',
  exclude: 'Left out',
  both_right: 'Both right',
  keep_literal: 'Kept as its own value',
  reconfirm: 'Reconfirmed',
  revise: 'Revised',
  withdraw: 'Withdrawn',
};

// The resolutions whose change to the row the rows it carries may share.
const CARRIED_CHOICES = ['typed', 'join', 'move_there', 'exclude'];
const GROUP_ORDER = ['identifier', 'data_profile_id', 'drop'];

// What makes a prompt the same prompt on a later park (Spec 3 section 7.2): a
// choice made on it is kept only while these are unchanged.
function promptSignature(record) {
  return stableStringify({
    subject: record.subject, data_profile_id: record.data_profile_id, relevant_rows: record.relevant_rows,
    offered: record.offered, answer: record.answer, submission_id: record.submission_id,
  });
}

// Every carried row of a prompt, from the batch's roster, never only the ones
// the prompt lists (Spec 3 sections 2.1 and 5.2): the rows of the prompt's file
// under its identifier and profile with no details, its own row left out. The
// rows the prompt lists come first, in its order (the walk's); the rest follow
// unit by unit, each unit's rows in their order.
function carriedRecords(prompt, roster) {
  const listed = new Map(prompt.carried.map((ref, index) => [canonicalJson(ref), index]));
  const placed = [];
  for (const record of roster.values()) {
    const carried = record.id !== prompt.id && record.subject.kind === 'row'
      && record.subject.file_id === prompt.subject.file_id
      && record.identifier === prompt.identifier && record.data_profile_id === prompt.data_profile_id
      && Object.values(record.this_row).every((value) => value === null);
    if (!carried) continue;
    const position = listed.get(canonicalJson(ownReference(record.subject)));
    placed.push({ record, listedAt: position === undefined ? listed.size : position });
  }
  placed.sort((first, second) => {
    if (first.listedAt !== second.listedAt) return first.listedAt - second.listedAt;
    for (const field of ['unit_id', 'row', 'period_label', 'page', 'record']) {
      const one = first.record.subject[field];
      const other = second.record.subject[field];
      if (one === other) continue;
      if (one === null) return -1;
      if (other === null) return 1;
      return one < other ? -1 : 1;
    }
    return 0;
  });
  return placed.map((item) => item.record);
}

// The words the dialog names rows, subjects, profiles and values with.
function makeLabels(table, fileNames, profiles) {
  const tableName = formatText(table.name);
  const identifierName = targetLabel(table.identifier.name);
  const profile = (profileId) => profiles.get(profileId)?.name || profileId;
  const row = (ref) => {
    let key = { row: ref.row };
    if (ref.page !== null && ref.page !== undefined) key = { page: ref.page, record: ref.record };
    else if (ref.period_label) key = { row: ref.row, period: ref.period_label };
    return `${fileNames[ref.file_id] || 'Another file'}, ${rowKeyText(key)}`;
  };
  const identifier = (value) => (value == null || String(value).startsWith(PLACEHOLDER_PREFIX)
    ? `no ${identifierName}` : value);
  const subject = (record) => {
    if (record.subject.kind === 'row') return row(record.subject);
    return `${record.subject.identifier} in ${tableName} under ${profile(record.subject.data_profile_id)}`;
  };
  const value = (item) => {
    if (item === null || item === undefined) return '—';
    if (typeof item === 'boolean') return item ? 'Yes' : 'No';
    return String(item);
  };
  const group = (name) => {
    if (name === 'identifier') return identifierName;
    if (name === 'data_profile_id') return 'profile and attributes';
    return 'leaving out';
  };
  return { tableName, identifierName, profile, row, identifier, subject, value, group };
}

// The table's data profiles by id ({ name, status }), read once while open.
function useProfiles(open, companyId, tableId) {
  const [profiles, setProfiles] = useState(new Map());
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getDocs(collection(db, `${tableScope(companyId, tableId)}/data_profiles`)).then((snapshot) => {
      if (cancelled) return;
      setProfiles(new Map(snapshot.docs.map((profile) => [profile.id, profile.data()])));
    }).catch((error) => {
      console.error('Error reading the data profiles for the questions dialog:', error);
    });
    return () => { cancelled = true; };
  }, [open, companyId, tableId]);
  return profiles;
}

// ── Showing a prompt ───────────────────────────────────────────────────────

// Rows side by side: their identifier, profile, details and attributes, a
// value that differs from the first row's marked.
function RowsTable({ rows, table, labels }) {
  const details = table.detailColumns.map((column) => column.name);
  const attributes = table.attributeColumns.map((column) => column.name);
  const [first] = rows;
  const cell = (row, kind, name) => {
    if (kind === 'identifier') return row.identifier;
    if (kind === 'profile') return row.data_profile_id;
    return (kind === 'detail' ? row.details : row.attributes)?.[name] ?? null;
  };
  const columns = [
    ['identifier', null, labels.identifierName], ['profile', null, 'Profile'],
    ...details.map((name) => ['detail', name, targetLabel(name)]),
    ...attributes.map((name) => ['attribute', name, formatText(name)]),
  ];
  return (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {columns.map(([kind, name, title]) => <TableCell key={`${kind}:${name}`}>{title}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell component="th" scope="row">{row.label}</TableCell>
              {columns.map(([kind, name]) => {
                const value = cell(row, kind, name);
                const differs = row !== first && stableStringify(value) !== stableStringify(cell(first, kind, name));
                let text = labels.value(value);
                if (kind === 'identifier') text = labels.identifier(value);
                if (kind === 'profile') text = labels.profile(value);
                return (
                  <TableCell key={`${kind}:${name}`} sx={differs ? { fontWeight: 700, color: 'warning.dark' } : undefined}>
                    {text}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// An ordinary prompt's Conflicts: the row, what it was compared with, what it looks like.
function Conflicts({ prompt, labels }) {
  const lines = [];
  const thisRow = prompt.subject.kind === 'row' ? labels.row(prompt.subject) : labels.subject(prompt);
  if (String(prompt.identifier).startsWith(PLACEHOLDER_PREFIX)) {
    lines.push(`${thisRow} has no ${labels.identifierName}.`);
  } else {
    lines.push(`${thisRow} says ${prompt.identifier} under ${labels.profile(prompt.data_profile_id)}.`);
  }
  const compared = prompt.compared_with;
  if (compared) {
    const under = `${compared.identifier} under ${labels.profile(compared.data_profile_id)}`;
    if (compared.belief_count > 0) {
      lines.push(`${labels.tableName} already has ${under}, from ${countNoun(compared.file_count, 'other file')}.`);
    }
    compared.batch_rows.forEach((row) => lines.push(`${labels.row(row)} already uses ${under}.`));
    if (compared.batch_row_count > compared.batch_rows.length) {
      lines.push(`${countNoun(compared.batch_row_count - compared.batch_rows.length, 'more row')} of this check also use it.`);
    }
  }
  prompt.look_alikes.forEach((alike) => {
    const label = Object.hasOwn(alike.source, 'source') ? alike.identifier : `${labels.row(alike.source)} (${alike.identifier})`;
    lines.push(`${thisRow}'s details match ${label} under ${labels.profile(alike.data_profile_id)}.`);
  });
  return (
    <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
      {lines.map((line) => <Typography component="li" variant="body2" key={line}>{line}</Typography>)}
    </Box>
  );
}

// One resolution, checkbox-style: checking it clears the prompt's others.
function Resolution({ checked, disabled, label, consequence, onToggle, children }) {
  return (
    <Box sx={{ mb: 0.5 }}>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={onToggle} disabled={disabled} />}
        label={(
          <Box>
            <Typography variant="body2">{label}</Typography>
            {consequence && <Typography variant="caption" color="text.secondary">{consequence}</Typography>}
          </Box>
        )}
      />
      {checked && children && <Box sx={{ pl: 4, pb: 1 }}>{children}</Box>}
    </Box>
  );
}

// An ordinary prompt: its Conflicts, the rows side by side, and its Resolutions.
function PromptCard({ prompt, roster, bySubject, selection, disabled, problem, changed, table, labels, onSelect }) {
  const [carriedShown, setCarriedShown] = useState(CARRIED_PAGE_SIZE);
  const offered = prompt.offered;
  const thisRow = labels.row(prompt.subject);
  const identifier = labels.identifier(prompt.identifier);
  const profile = labels.profile(prompt.data_profile_id);
  const compared = prompt.compared_with;
  const recordOf = (subject) => bySubject.get(canonicalJson(subject));
  // The comparison's sources a move there or both right can take from: each
  // original belief and each batch row shown.
  const comparisonSources = compared ? [
    ...compared.beliefs.map((belief) => recordOf({ kind: 'belief_source', ...belief.source })),
    ...compared.batch_rows.map((row) => recordOf({ kind: 'row', ...ownReference({ kind: 'row', ...row }) })),
  ].filter(Boolean) : [];
  const lookAlikes = prompt.look_alikes
    .map((alike) => ({ alike, record: recordOf(subjectOfReference(alike.source)) }))
    .filter(({ record }) => record !== undefined);
  const choose = (key, choice, fields = {}) => {
    if (selection?.key === key) onSelect(null);
    else {
      onSelect({
        key, choice, identifier: '', sourceId: null, moveBeliefs: false, targetIds: [], carried: null, carriedIds: [],
        ...fields,
      });
    }
  };
  const update = (fields) => onSelect({ ...selection, ...fields });
  const checked = (key) => selection?.key === key;
  const otherFiles = (count) => countNoun(count, 'other file');
  // The rows shown side by side: this row, then what it was compared with and what it looks like.
  const rows = [{ key: 'this', label: 'This row', identifier: prompt.identifier, data_profile_id: prompt.data_profile_id,
    details: prompt.this_row, attributes: prompt.attributes }];
  if (compared) {
    compared.beliefs.forEach((belief) => rows.push({
      key: `belief:${canonicalJson(belief.source)}`, label: `In ${labels.tableName}`, identifier: compared.identifier,
      data_profile_id: compared.data_profile_id, details: belief.details, attributes: belief.attributes,
    }));
    compared.batch_rows.forEach((row) => rows.push({
      key: `row:${canonicalJson(ownReference({ kind: 'row', ...row }))}`, label: labels.row(row), ...row,
    }));
  }
  prompt.look_alikes.forEach((alike) => rows.push({
    key: `alike:${canonicalJson(alike.source)}`, label: `Looks like (${Math.round(alike.similarity * 100)}%)`,
    identifier: alike.identifier, data_profile_id: alike.data_profile_id, details: alike.details, attributes: alike.attributes,
  }));

  const sourcePicker = (
    <RadioGroup value={selection?.sourceId || ''} onChange={(event) => update({ sourceId: event.target.value })}>
      {comparisonSources.map((source) => (
        <FormControlLabel
          key={source.id}
          value={source.id}
          control={<Radio size="small" disabled={disabled} />}
          label={`${labels.subject(source)} · ${Object.entries(source.attributes)
            .map(([name, value]) => `${formatText(name)}: ${labels.value(value)}`).join(', ')}`}
        />
      ))}
    </RadioGroup>
  );
  const mandatoryDetails = table.detailColumns
    .filter((column) => column.mandatory_per_file || column.mandatory_per_unit)
    .map((column) => targetLabel(column.name));
  // The carried rows to tick, every one from the roster, a page at a time.
  const tickable = selection?.carried === 'selected' && roster !== null ? carriedRecords(prompt, roster) : [];
  const carriedControl = selection && CARRIED_CHOICES.includes(selection.choice) && prompt.carried_count > 0 && (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2">
        {countNoun(prompt.carried_count, 'more row')} of this file read {identifier} under {profile} with no details.
        Give them the same answer:
      </Typography>
      <RadioGroup row value={selection.carried || ''} onChange={(event) => update({ carried: event.target.value })}>
        <FormControlLabel value="all" control={<Radio size="small" disabled={disabled} />} label={`All ${prompt.carried_count}`} />
        <FormControlLabel value="selected" control={<Radio size="small" disabled={disabled} />} label="The rows I tick" />
        <FormControlLabel value="none" control={<Radio size="small" disabled={disabled} />} label="None of them" />
      </RadioGroup>
      {tickable.slice(0, carriedShown).map((carriedRecord) => {
        const ticked = selection.carriedIds.includes(carriedRecord.id);
        return (
          <FormControlLabel
            key={carriedRecord.id}
            control={(
              <Checkbox
                size="small"
                checked={ticked}
                disabled={disabled}
                onChange={() => update({
                  carriedIds: ticked
                    ? selection.carriedIds.filter((id) => id !== carriedRecord.id)
                    : [...selection.carriedIds, carriedRecord.id],
                })}
              />
            )}
            label={labels.row(carriedRecord.subject)}
          />
        );
      })}
      {tickable.length > carriedShown && (
        <Button size="small" onClick={() => setCarriedShown((shown) => shown + CARRIED_PAGE_SIZE)}>Show more rows</Button>
      )}
      {mandatoryDetails.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          These rows have no details of their own. {mandatoryDetails.join(', ')} must come from another row of this file
          under the identifier they end up with, or the check fails the file.
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} role="group" aria-label={thisRow}>
      {changed && <Alert severity="info" sx={{ mb: 1 }}>{CHANGED_SINCE_CHOSEN}</Alert>}
      <Typography variant="overline">Conflicts</Typography>
      <Conflicts prompt={prompt} labels={labels} />
      <RowsTable rows={rows} table={table} labels={labels} />
      {compared && compared.units.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {compared.identifier} comes from {[...new Set(compared.units.map((unit) => unit.file_name))].join(', ')}
          {compared.unit_count > compared.units.length ? ` and ${countNoun(compared.unit_count - compared.units.length, 'more table')}` : ''}.
        </Typography>
      )}
      <Typography variant="overline">Resolutions</Typography>
      {offered.includes('own') && (
        <Resolution
          checked={checked('own')}
          disabled={disabled}
          label={`${thisRow} is right as ${identifier}.`}
          consequence={`${thisRow} keeps ${identifier} under ${profile}; no other row changes.`}
          onToggle={() => choose('own', 'own')}
        />
      )}
      {offered.includes('both_right') && (
        <Resolution
          checked={checked('both_right')}
          disabled={disabled}
          label={`Both are right: ${thisRow} is the same ${labels.identifierName} as the row you pick.`}
          consequence={`${thisRow} stays under ${identifier}; ${compared ? otherFiles(compared.file_count) : 'no file'} already hold it.`}
          onToggle={() => choose('both_right', 'both_right')}
        >
          {sourcePicker}
        </Resolution>
      )}
      {offered.includes('move_here') && compared && (
        <Resolution
          checked={checked('move_here')}
          disabled={disabled}
          label={`Keep ${thisRow} as ${identifier} in ${profile}; move the rows you tick to ${profile} and give them ${thisRow}'s attributes.`}
          consequence={`${compared.identifier} under ${labels.profile(compared.data_profile_id)} is in ${otherFiles(compared.file_count)}.`}
          onToggle={() => choose('move_here', 'move_here')}
        >
          {compared.belief_count > 0 && (
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(selection?.moveBeliefs)}
                  disabled={disabled}
                  onChange={() => update({ moveBeliefs: !selection.moveBeliefs })}
                />
              )}
              label={`The landed rows of ${compared.identifier} in ${labels.tableName} (${countNoun(compared.belief_count, 'source')})`}
            />
          )}
          {compared.batch_rows.map((row) => {
            const rowRecord = recordOf({ kind: 'row', ...ownReference({ kind: 'row', ...row }) });
            if (rowRecord === undefined) return null;
            const ticked = Boolean(selection?.targetIds.includes(rowRecord.id));
            return (
              <FormControlLabel
                key={rowRecord.id}
                control={(
                  <Checkbox
                    size="small"
                    checked={ticked}
                    disabled={disabled}
                    onChange={() => update({
                      targetIds: ticked ? selection.targetIds.filter((id) => id !== rowRecord.id) : [...selection.targetIds, rowRecord.id],
                    })}
                  />
                )}
                label={labels.row(row)}
              />
            );
          })}
        </Resolution>
      )}
      {offered.includes('typed') && (
        <Resolution
          checked={checked('typed')}
          disabled={disabled}
          label={`Give ${thisRow} this identifier:`}
          consequence={`${thisRow} is filed under the identifier you type.`}
          onToggle={() => choose('typed', 'typed')}
        >
          <TextField
            size="small"
            value={selection?.identifier || ''}
            disabled={disabled}
            inputProps={{ 'aria-label': `The ${labels.identifierName} for ${thisRow}` }}
            onChange={(event) => update({ identifier: event.target.value })}
          />
          {carriedControl}
        </Resolution>
      )}
      {offered.includes('join') && lookAlikes.map(({ alike, record: alikeRecord }) => (
        <Resolution
          key={`join:${alikeRecord.id}`}
          checked={checked(`join:${alikeRecord.id}`)}
          disabled={disabled}
          label={`Give ${thisRow} the listed identifier ${alike.identifier}.`}
          consequence={`${thisRow} joins ${alike.identifier}, which is in ${otherFiles(alike.file_count)}.`}
          onToggle={() => choose(`join:${alikeRecord.id}`, 'join', { sourceId: alikeRecord.id })}
        >
          {carriedControl}
        </Resolution>
      ))}
      {offered.includes('pull') && lookAlikes.map(({ alike, record: alikeRecord }) => (
        <Resolution
          key={`pull:${alikeRecord.id}`}
          checked={checked(`pull:${alikeRecord.id}`)}
          disabled={disabled}
          label={`Keep ${thisRow} as ${identifier}; ${labels.subject(alikeRecord)} is the same and takes ${identifier} too.`}
          consequence={`${labels.subject(alikeRecord)} changes, in ${otherFiles(alike.file_count)}.`}
          onToggle={() => choose(`pull:${alikeRecord.id}`, 'pull', { sourceId: alikeRecord.id })}
        />
      ))}
      {offered.includes('move_there') && compared && (
        <Resolution
          checked={checked('move_there')}
          disabled={disabled}
          label={`Keep ${thisRow} as ${identifier}; move ${thisRow} to ${labels.profile(compared.data_profile_id)} with the selected original comparison source's attributes.`}
          consequence={`Only ${thisRow} changes.`}
          onToggle={() => choose('move_there', 'move_there')}
        >
          {sourcePicker}
          {carriedControl}
        </Resolution>
      )}
      {offered.includes('exclude') && (
        <Resolution
          checked={checked('exclude')}
          disabled={disabled}
          label={`Leave ${thisRow} out.`}
          consequence={`${thisRow} is not added to ${labels.tableName}.`}
          onToggle={() => choose('exclude', 'exclude')}
        >
          {carriedControl}
        </Resolution>
      )}
      {problem && <Typography variant="body2" color="error">{problem}</Typography>}
    </Paper>
  );
}

// A prompt whose saved answer's rows changed: per affected assignment, the
// saved and current rows, and what to do with it.
function EvidenceChangedCard({ prompt, roster, bySubject, selection, disabled, problem, changed, table, labels, onSelect }) {
  const groups = selection?.groups || {};
  const affected = affectedGroups(prompt);
  const ownNow = ownEvidence(prompt);
  // The sources that hold this belief's identifier under another profile now.
  const owners = prompt.subject.kind === 'belief_source' ? prompt.relevant_rows
    .filter((row) => Object.hasOwn(row.ref, 'source') && row.ref.source.identifier === prompt.subject.identifier
      && canonicalJson(row.ref) !== canonicalJson(ownReference(prompt.subject)))
    .map((row) => bySubject.get(canonicalJson(subjectOfReference(row.ref))))
    .filter(Boolean) : [];
  const setGroup = (group, groupChoice) => {
    const next = { ...groups };
    if (groupChoice === null) delete next[group];
    else next[group] = groupChoice;
    onSelect(Object.keys(next).length === 0 ? null : { groups: next });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} role="group" aria-label={labels.subject(prompt)}>
      <Typography variant="body2" sx={{ mb: 1 }}>{labels.subject(prompt)}</Typography>
      {changed && <Alert severity="info" sx={{ mb: 1 }}>{CHANGED_SINCE_CHOSEN}</Alert>}
      {prompt.notes.map((note) => <Alert key={note} severity="warning" sx={{ mb: 1 }}>{note}</Alert>)}
      {affected.map(({ group, slot, saved, current }) => {
        const chosen = groups[group] || null;
        const trigger = labels.row(slot.basis.trigger);
        const source = slot.reference !== null ? bySubject.get(canonicalJson(subjectOfReference(slot.reference))) : undefined;
        // A selected landed source whose identifier another profile now holds:
        // the reference no longer describes a source, so the value cannot be
        // reconfirmed as copied from it (Spec 3 section 2.3: a value reference
        // is retained only while it still describes the chosen value).
        const selectedMoved = slot.reference !== null && Object.hasOwn(slot.reference, 'source')
          && prompt.relevant_rows.some((row) => Object.hasOwn(row.ref, 'source')
            && row.ref.source.identifier === slot.reference.source.identifier
            && canonicalJson(row.ref.source) !== canonicalJson(slot.reference.source));
        const savedValue = group === 'data_profile_id' ? labels.profile(slot.value) : labels.value(slot.value);
        // The rows the group is bound to, as they read now, other than the
        // subject's own: what a revised profile bundle can move the subject to
        // (a move there with a selected source, Spec 3 sections 2.3 and 7.2).
        const destinations = current
          .filter((row) => canonicalJson(row.ref) !== canonicalJson(ownReference(prompt.subject)))
          .map((row) => bySubject.get(canonicalJson(subjectOfReference(row.ref))))
          .filter(Boolean);
        const option = (key, choice, label, fields = {}) => (
          <Resolution
            key={key}
            checked={chosen?.key === key}
            disabled={disabled}
            label={label}
            onToggle={() => setGroup(group, chosen?.key === key ? null : {
              key, choice, identifier: '', ownerId: null, sourceId: null, ...fields,
            })}
          >
            {choice === 'revise' && group === 'identifier' && (
              <TextField
                size="small"
                value={chosen?.identifier || ''}
                disabled={disabled}
                inputProps={{ 'aria-label': `The new ${labels.identifierName}` }}
                onChange={(event) => setGroup(group, { ...chosen, identifier: event.target.value })}
              />
            )}
            {choice === 'revise' && group === 'data_profile_id' && (
              <RadioGroup value={chosen?.sourceId || ''} onChange={(event) => setGroup(group, { ...chosen, sourceId: event.target.value })}>
                {destinations.map((destination) => (
                  <FormControlLabel
                    key={destination.id}
                    value={destination.id}
                    control={<Radio size="small" disabled={disabled} />}
                    label={`${labels.subject(destination)} · ${labels.profile(destination.data_profile_id)} (${Object.entries(
                      destination.attributes,
                    ).map(([name, value]) => `${formatText(name)}: ${labels.value(value)}`).join(', ')})`}
                  />
                ))}
              </RadioGroup>
            )}
          </Resolution>
        );
        return (
          <Box key={group} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Saved: {labels.group(group)} {savedValue}
              {source ? `, copied from ${labels.subject(source)}` : ''}, for {trigger}.
            </Typography>
            <RowsTable
              rows={[
                ...saved.map((row) => ({ ...row, key: `saved:${canonicalJson(row)}`,
                  label: `As saved: ${labels.subject({ subject: subjectOfReference(row.ref) })}` })),
                ...current.map((row) => ({ ...row, key: `now:${canonicalJson(row)}`,
                  label: `Now: ${labels.subject({ subject: subjectOfReference(row.ref) })}` })),
              ]}
              table={table}
              labels={labels}
            />
            {ownNow !== null && (slot.reference === null || (source !== undefined && !selectedMoved))
              && option('reconfirm', 'reconfirm', `Keep it: ${savedValue}, checked against the rows as they read now.`)}
            {ownNow !== null && group === 'identifier' && prompt.offered.includes('revise')
              && option('revise', 'revise', 'Give it this identifier instead:')}
            {ownNow !== null && group === 'data_profile_id' && prompt.offered.includes('revise') && destinations.length > 0
              && option('revise', 'revise', 'Give it the profile and attributes of the source you pick instead:')}
            {ownNow !== null && slot.reference !== null && prompt.offered.includes('keep_literal')
              && option('keep_literal', 'keep_literal', `Keep ${savedValue} on its own, no longer copied from another row.`)}
            {option('withdraw', 'withdraw', 'Withdraw it.')}
            {group === 'identifier' && prompt.offered.includes('pull') && owners.map((owner) => option(
              `pull:${owner.id}`, 'pull', `Give ${labels.subject(owner)} ${savedValue} instead.`, { ownerId: owner.id },
            ))}
            {group === 'identifier' && prompt.offered.includes('move_here') && owners.map((owner) => option(
              `move_here:${owner.id}`, 'move_here',
              `Give ${labels.subject(owner)} ${savedValue} instead, and move it to ${trigger}'s profile with its attributes.`,
              { ownerId: owner.id },
            ))}
          </Box>
        );
      })}
      {problem && <Typography variant="body2" color="error">{problem}</Typography>}
    </Paper>
  );
}

// ── Answering a parked batch ───────────────────────────────────────────────

function OpenQuestions({ open, companyId, tableId, table, batch, fileNames, onClose }) {
  const { currentUser } = useAuth();
  const profiles = useProfiles(open, companyId, tableId);
  const labels = useMemo(() => makeLabels(table, fileNames, profiles), [table, fileNames, profiles]);

  // Each time the batch parks the questions are read again.
  const [park, setPark] = useState(0);
  const previousStatus = useRef(batch.status);
  useEffect(() => {
    if (batch.status === 'parked' && previousStatus.current !== 'parked') setPark((value) => value + 1);
    previousStatus.current = batch.status;
  }, [batch.status]);
  const [reloads, setReloads] = useState(0);
  const [loaded, setLoaded] = useState({ park: -1, prompts: [], next: null, roster: null, error: false });
  const [loadingMore, setLoadingMore] = useState(false);

  // { promptId: selection }: the person's choices, kept until submitted or
  // until the prompt changes on a later park.
  const [selections, setSelections] = useState({});
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;
  const [reread, setReread] = useState(new Set());
  // { '<qid>\u0000<group>': 'revise' | 'keep_literal' | 'withdraw' }: decisions on dependent saved answers.
  const [decisions, setDecisions] = useState({});
  const [drafting, setDrafting] = useState({
    drafts: new Map(), conflicts: [], dependents: [], undecided: 0, problems: {}, ready: false,
  });
  const [submission, setSubmission] = useState({ phase: 'idle' });
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.all([
      pageQuestions(companyId, tableId, batch.id, { openOnly: true }),
      loadRoster(companyId, tableId, batch.id),
    ]).then(([page, roster]) => {
      if (cancelled) return;
      // A choice outlives a park only where its prompt is unchanged.
      const kept = {};
      const changed = new Set();
      for (const [promptId, selection] of Object.entries(selectionsRef.current)) {
        const record = roster.get(promptId);
        if (record !== undefined && record.open_at != null && promptSignature(record) === selection.signature) {
          kept[promptId] = selection;
        } else {
          changed.add(promptId);
        }
      }
      setSelections(kept);
      setReread(changed);
      setDecisions({});
      setLoaded({ park, prompts: page.records, next: page.next, roster, error: false });
    }).catch((error) => {
      if (cancelled) return;
      console.error('Error reading the questions of a check:', error);
      setLoaded((previous) => ({ ...previous, error: true }));
    });
    return () => { cancelled = true; };
  }, [open, companyId, tableId, batch.id, park, reloads]);

  const roster = loaded.park === park ? loaded.roster : null;
  const bySubject = useMemo(() => (roster === null ? new Map() : recordsBySubject(roster)), [roster]);

  // The drafts the choices make, the conflicts between them and the saved
  // answers they disturb, recomputed whenever a choice or a decision changes.
  useEffect(() => {
    if (roster === null) return undefined;
    let cancelled = false;
    setDrafting((previous) => ({ ...previous, ready: false }));
    (async () => {
      const changes = [];
      const problems = {};
      for (const [promptId, selection] of Object.entries(selections)) {
        const prompt = roster.get(promptId);
        if (prompt === undefined) continue;
        const requests = [];
        if (prompt.kind === 'evidence_changed') {
          for (const [group, groupChoice] of Object.entries(selection.groups)) {
            const source = roster.get(groupChoice.sourceId) || null;
            if (groupChoice.choice === 'revise' && group === 'data_profile_id' && source === null) {
              problems[promptId] = CHOOSE_SOURCE;
              continue;
            }
            requests.push({
              choice: groupChoice.choice,
              selection: { group, identifier: groupChoice.identifier, owner: roster.get(groupChoice.ownerId), source },
            });
          }
        } else {
          let carried = [];
          if (CARRIED_CHOICES.includes(selection.choice) && prompt.carried_count > 0) {
            if (selection.carried === null) {
              problems[promptId] = CHOOSE_CARRIED;
              continue;
            }
            if (selection.carried === 'all') {
              carried = carriedRecords(prompt, roster);
            } else if (selection.carried === 'selected') {
              carried = selection.carriedIds.map((id) => roster.get(id)).filter(Boolean);
            }
          }
          let targets = [];
          if (selection.choice === 'move_here') {
            const compared = prompt.compared_with;
            if (selection.moveBeliefs) {
              // The comparison's whole group of original belief sources.
              targets = [...roster.values()].filter((record) => record.subject.kind === 'belief_source'
                && record.identifier === compared.identifier && record.data_profile_id === compared.data_profile_id);
            }
            targets.push(...selection.targetIds.map((id) => roster.get(id)).filter(Boolean));
            if (targets.length === 0) {
              problems[promptId] = CHOOSE_TARGETS;
              continue;
            }
          }
          const source = selection.sourceId === null ? null : roster.get(selection.sourceId) || null;
          if (['join', 'pull', 'move_there', 'both_right'].includes(selection.choice) && source === null) {
            problems[promptId] = CHOOSE_SOURCE;
            continue;
          }
          requests.push({ choice: selection.choice, selection: { identifier: selection.identifier, source, targets, carried } });
        }
        try {
          for (const request of requests) changes.push(...await resolveChoice(prompt, request.choice, request.selection, roster));
        } catch (error) {
          problems[promptId] = gestureFailureMessage(error);
        }
      }
      // The person's decision on a disturbed slot is that slot's one
      // replacement: it takes the place of any change a choice made to the
      // slot's group, decided on the slot as proposed. Decided slots join the
      // drafts, which can disturb others in turn; each decision is applied
      // once, and a rewrite only while the slot can still be rewritten.
      const decided = new Map();
      const decidedDependents = [];
      let { drafts, conflicts } = draftAnswers(changes, roster);
      let dependents = dependentSlots(drafts, roster);
      for (;;) {
        const newlyDecided = dependents.filter((dependent) => {
          const key = `${dependent.questionId}\u0000${dependent.group}`;
          const decision = decisions[key];
          if (decision === undefined || decided.has(key)) return false;
          return decision !== 'revise' || dependent.rewriteTo !== null;
        });
        if (newlyDecided.length === 0) break;
        for (const dependent of newlyDecided) {
          const key = `${dependent.questionId}\u0000${dependent.group}`;
          const decision = decisions[key];
          const record = roster.get(dependent.questionId);
          const proposed = { ...record, answer: drafts.get(dependent.questionId) || record.answer };
          decided.set(key, await resolveChoice(proposed, decision, {
            group: dependent.group, values: decision === 'revise' ? dependent.rewriteTo : null,
          }, roster));
          decidedDependents.push(dependent);
        }
        const kept = changes.filter((change) => !decided.has(`${change.questionId}\u0000${change.group}`));
        ({ drafts, conflicts } = draftAnswers([...kept, ...[...decided.values()].flat()], roster));
        dependents = dependentSlots(drafts, roster);
      }
      if (!cancelled) {
        // Every slot still disturbed needs a decision, a decided one included.
        const undecidedShown = dependents.filter((dependent) => !decided.has(`${dependent.questionId}\u0000${dependent.group}`));
        setDrafting({
          drafts, conflicts, dependents: [...decidedDependents, ...undecidedShown], undecided: dependents.length,
          problems, ready: true,
        });
      }
    })().catch((error) => {
      if (cancelled) return;
      setDrafting({
        drafts: new Map(), conflicts: [], dependents: [], undecided: 0, problems: { '': gestureFailureMessage(error) }, ready: true,
      });
    });
    return () => { cancelled = true; };
  }, [roster, selections, decisions]);

  // ── What the person may do now ──────────────────────────────────────────
  const reservation = batch.submitting;
  const currentAttempt = reservation != null && attemptRef.current !== null && reservation.id === attemptRef.current.id;
  // Any reservation but this attempt's holds the batch; it is another user's
  // unless the signed-in person made it (an attempt from before the dialog
  // was opened again, or from another window).
  const heldByOther = reservation != null && !currentAttempt;
  const anotherUsers = heldByOther && reservation.by !== currentUser?.uid;
  const heldAttempt = submission.phase === 'failed' && submission.retry;
  const answerable = batch.status === 'parked' && !heldByOther && submission.phase !== 'submitting' && !heldAttempt;
  // The drafts that change their subject's answer: only these are submitted,
  // since an untouched question takes no write (Spec 3 section 7.1).
  const changedDrafts = new Map(roster === null ? [] : [...drafting.drafts]
    .filter(([id, draft]) => stableStringify(draft) !== stableStringify(replacementAnswer(roster.get(id)))));
  const canSubmit = answerable && roster !== null && drafting.ready && changedDrafts.size > 0
    && drafting.conflicts.length === 0 && drafting.undecided === 0 && Object.keys(drafting.problems).length === 0;

  const select = (promptId, selection) => {
    setSelections((previous) => {
      const next = { ...previous };
      if (selection === null) delete next[promptId];
      else next[promptId] = { ...selection, signature: promptSignature(roster.get(promptId)) };
      return next;
    });
    setReread((previous) => {
      if (!previous.has(promptId)) return previous;
      const next = new Set(previous);
      next.delete(promptId);
      return next;
    });
  };

  const submit = async () => {
    if (attemptRef.current === null) attemptRef.current = newSubmission(changedDrafts, roster);
    const attempt = attemptRef.current;
    setSubmission({ phase: 'submitting', staged: attempt.staged.size, total: attempt.drafts.size });
    try {
      const outcome = await submitAnswers(companyId, tableId, batch.id, attempt, {
        onProgress: (staged, total) => {
          if (mountedRef.current) setSubmission({ phase: 'submitting', staged, total });
        },
      });
      if (!mountedRef.current) return;
      attemptRef.current = null;
      if (outcome === 'accepted') {
        onClose();
        return;
      }
      setSubmission({ phase: 'not_saved' });
    } catch (error) {
      if (!mountedRef.current) return;
      // An attempt that still holds the batch is retried as it is.
      if (!attempt.claimed) attemptRef.current = null;
      setSubmission({ phase: 'failed', message: gestureFailureMessage(error), retry: attempt.claimed });
    }
  };

  const loadMore = async () => {
    const parkAtStart = loaded.park;
    setLoadingMore(true);
    try {
      const page = await pageQuestions(companyId, tableId, batch.id, { openOnly: true, after: loaded.next });
      if (!mountedRef.current) return;
      setLoaded((previous) => (previous.park === parkAtStart
        ? { ...previous, prompts: [...previous.prompts, ...page.records], next: page.next } : previous));
    } catch (error) {
      console.error('Error reading more questions of a check:', error);
    } finally {
      if (mountedRef.current) setLoadingMore(false);
    }
  };

  // ── Showing it ──────────────────────────────────────────────────────────
  let status = null;
  if (anotherUsers && submission.phase !== 'not_saved') {
    status = <Alert severity="warning" sx={{ mb: 2 }}>{ANOTHER_SUBMISSION}</Alert>;
  }
  let outcome = null;
  if (submission.phase === 'submitting') {
    outcome = (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2">Saving your answers: {submission.staged} of {submission.total}</Typography>
        <LinearProgress variant="determinate" value={submission.total ? (100 * submission.staged) / submission.total : 0} />
      </Box>
    );
  } else if (submission.phase === 'failed') {
    outcome = (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={submission.retry ? <Button color="inherit" size="small" onClick={submit}>Try again</Button> : undefined}
      >
        {submission.message}
      </Alert>
    );
  } else if (submission.phase === 'not_saved') {
    outcome = <Alert severity="warning" sx={{ mb: 2 }}>{NOT_SAVED}</Alert>;
  }

  const shown = loaded.park === park ? loaded.prompts.map((prompt) => roster?.get(prompt.id) || prompt) : [];
  const cardProps = (prompt) => ({
    prompt, roster, bySubject, table, labels,
    selection: selections[prompt.id] || null, disabled: !answerable || roster === null,
    problem: drafting.problems[prompt.id], changed: reread.has(prompt.id),
    onSelect: (selection) => select(prompt.id, selection),
  });

  return (
    <Dialog open={open} onClose={() => submission.phase !== 'submitting' && onClose()} maxWidth="lg" fullWidth>
      <DialogContent dividers>
        {status}
        {outcome}
        {loaded.error && (
          <Alert severity="error" action={<Button color="inherit" onClick={() => setReloads((value) => value + 1)}>Retry</Button>}>
            The questions could not be loaded.
          </Alert>
        )}
        {!loaded.error && loaded.park !== park && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {shown.map((prompt) => (prompt.kind === 'evidence_changed'
          ? <EvidenceChangedCard key={prompt.id} {...cardProps(prompt)} />
          : <PromptCard key={prompt.id} {...cardProps(prompt)} />))}
        {loaded.park === park && loaded.next !== null && (
          <Button onClick={loadMore} disabled={loadingMore}>Show more questions</Button>
        )}
        {drafting.conflicts.map((conflict) => (
          <Alert key={`${conflict.questionId}:${conflict.group}`} severity="error" sx={{ mb: 1 }}>
            Two of your answers change {labels.subject(roster.get(conflict.questionId))}&apos;s {labels.group(conflict.group)} differently.
            Change one of them so they agree.
          </Alert>
        ))}
        {drafting.dependents.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            {drafting.dependents.map((dependent) => {
              const key = `${dependent.questionId}\u0000${dependent.group}`;
              const dependentRecord = roster.get(dependent.questionId);
              const savedValue = dependent.group === 'data_profile_id'
                ? labels.profile(dependent.slot.value) : labels.value(dependent.slot.value);
              const why = dependent.reasons.map((reason) => (reason.kind === 'reference'
                ? `copied from ${labels.subject(roster.get(reason.questionId))}`
                : `included through ${labels.subject(roster.get(reason.questionId))}'s ${labels.group(reason.field)}`)).join(' and ');
              const decide = (decision) => setDecisions((previous) => {
                const next = { ...previous };
                if (next[key] === decision) delete next[key];
                else next[key] = decision;
                return next;
              });
              let rewriteText = '';
              if (dependent.rewriteTo !== null) {
                rewriteText = dependent.group === 'identifier'
                  ? dependent.rewriteTo.identifier : labels.profile(dependent.rewriteTo.data_profile_id);
              }
              return (
                <Box key={key} sx={{ mb: 1.5 }} role="group" aria-label={`The ${labels.group(dependent.group)} of ${labels.subject(dependentRecord)}`}>
                  <Typography variant="body2">
                    {labels.subject(dependentRecord)}: its {labels.group(dependent.group)} {savedValue} was {why}, which your answers change.
                  </Typography>
                  {dependent.rewriteTo !== null && (
                    <Resolution checked={decisions[key] === 'revise'} disabled={!answerable} label={`Change it to ${rewriteText}.`} onToggle={() => decide('revise')} />
                  )}
                  <Resolution checked={decisions[key] === 'keep_literal'} disabled={!answerable} label={`Keep ${savedValue} on its own.`} onToggle={() => decide('keep_literal')} />
                  <Resolution checked={decisions[key] === 'withdraw'} disabled={!answerable} label="Withdraw it." onToggle={() => decide('withdraw')} />
                </Box>
              );
            })}
          </Paper>
        )}
        {drafting.problems[''] && <Alert severity="error">{drafting.problems['']}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submission.phase === 'submitting'}>Close</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>Submit answers</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── A landed question, read-only ───────────────────────────────────────────

// One question opened from a unit's correction records after landing: its
// subject and last prompt, its current assignments with each one's own
// decision, basis and saved rows (never the latest prompt's), and the
// statements that landed on the unit, grouped by action.
function LandedQuestion({ open, companyId, tableId, table, fileNames, readOnly, onClose }) {
  const { currentUser } = useAuth();
  const profiles = useProfiles(open, companyId, tableId);
  const labels = useMemo(() => makeLabels(table, fileNames || {}, profiles), [table, fileNames, profiles]);
  const { batchId, questionId, corrections } = readOnly;
  const [state, setState] = useState({ key: null, question: null, error: false });
  const key = `${batchId}/${questionId}`;
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getDoc(doc(db, `${tableScope(companyId, tableId)}/batches/${batchId}/questions/${questionId}`)).then((snapshot) => {
      if (!cancelled) setState({ key, question: snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null, error: !snapshot.exists() });
    }).catch((error) => {
      if (cancelled) return;
      console.error('Error reading a landed question:', error);
      setState({ key, question: null, error: true });
    });
    return () => { cancelled = true; };
  }, [open, companyId, tableId, batchId, questionId, key]);
  const question = state.key === key ? state.question : null;
  const who = (uid) => (uid === currentUser?.uid ? 'you' : 'another user');

  let body = <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (state.key === key && state.error) body = <Alert severity="error">This question could not be loaded.</Alert>;
  if (question !== null) {
    const answer = question.answer;
    const statements = {};
    for (const correction of corrections || []) {
      // A move_profile record that keeps its profile changed only the row's attributes.
      let kind = correction.action;
      if (kind === 'move_profile' && correction.from_profile === correction.to_profile) kind = 'correct_attributes';
      if (statements[kind] === undefined) statements[kind] = [];
      statements[kind].push(correction);
    }
    const statementText = (kind, correction) => {
      if (kind === 'correct_identifier') {
        return `${labels.row(correction)}: ${labels.identifier(correction.identifier)} → ${correction.to}`;
      }
      if (kind === 'move_profile') {
        return `${labels.row(correction)}: ${labels.profile(correction.from_profile)} → ${labels.profile(correction.to_profile)}`;
      }
      if (kind === 'correct_attributes') return labels.row(correction);
      return `${labels.row(correction)}: left out`;
    };
    body = (
      <>
        <Typography variant="subtitle2">{labels.subject(question)}</Typography>
        {question.kind && question.kind !== 'evidence_changed' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="overline">Conflicts</Typography>
            <Conflicts prompt={question} labels={labels} />
          </Box>
        )}
        {question.decided_by && (
          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            Last answered by {who(question.decided_by)}, {formatTimestamp(question.decided_at)}
          </Typography>
        )}
        <Typography variant="overline">Current assignments</Typography>
        {(answer == null || GROUP_ORDER.every((group) => answer[group] === null)) && (
          <Typography variant="body2">None.</Typography>
        )}
        {answer != null && GROUP_ORDER.filter((group) => answer[group] !== null).map((group) => {
          const slot = answer[group];
          let value = labels.value(slot.value);
          if (group === 'data_profile_id') {
            value = `${labels.profile(slot.value)} (${Object.entries(answer.attributes)
              .map(([name, attribute]) => `${formatText(name)}: ${labels.value(attribute?.value)}`).join(', ')})`;
          }
          if (group === 'drop') value = 'left out';
          const comparison = slot.basis.comparison;
          return (
            <Box key={group} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>{labels.group(group)}: {value}</Typography>
              <Typography variant="caption" display="block">
                {CHOICE_TEXT[slot.basis.choice] || slot.basis.choice}, for {labels.row(slot.basis.trigger)}
                {slot.decision ? `, by ${who(slot.decision.decided_by)}, ${formatTimestamp(slot.decision.decided_at)}` : ''}
              </Typography>
              <Typography variant="caption" display="block">Before: {labels.value(slot.basis.from)}</Typography>
              {comparison && (
                <Typography variant="caption" display="block">
                  Compared with {labels.identifier(comparison.identifier)} under {labels.profile(comparison.data_profile_id)}
                </Typography>
              )}
              <RowsTable
                rows={slot.basis.evidence_keys.filter((evidence) => answer.relevant_rows[evidence])
                  .map((evidence) => ({ ...answer.relevant_rows[evidence], key: evidence,
                    label: labels.subject({ subject: subjectOfReference(answer.relevant_rows[evidence].ref) }) }))}
                table={table}
                labels={labels}
              />
            </Box>
          );
        })}
        <Typography variant="overline">What landed</Typography>
        {Object.keys(statements).length === 0 && <Typography variant="body2">Nothing landed from this answer on this table.</Typography>}
        {Object.entries(statements).map(([kind, list]) => (
          <Box key={kind} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {{
                correct_identifier: 'Identifier corrected', move_profile: 'Moved to another profile',
                correct_attributes: 'Attributes corrected', drop_row: 'Left out',
              }[kind]}
            </Typography>
            {list.map((correction) => (
              <Typography key={`${correction.unit_id}:${canonicalJson(ownReference({ kind: 'row', ...correction }))}`} variant="caption" display="block">
                {statementText(kind, correction)} · by {who(correction.decided_by)}, {formatTimestamp(correction.decided_at)}
              </Typography>
            ))}
          </Box>
        ))}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent dividers>{body}</DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// Props: open, companyId, tableId, table, fileNames ({ fileId: name }), onClose;
// and either batch (the live batch document, with its id), to answer its
// questions, or readOnly ({ batchId, questionId, corrections: the unit's
// correction records of that question }) to show one landed question.
function QuestionsDialog(props) {
  if (props.readOnly) return <LandedQuestion {...props} />;
  return <OpenQuestions {...props} />;
}

export default QuestionsDialog;
